<template>
  <div class="product-page">
    <!-- 头部 -->
    <div class="page-header">
      <div class="header-left">
        <h1 class="page-title">
          <el-icon size="28" style="margin-right:8px;vertical-align:middle;color:#667eea;"><Goods /></el-icon>
          产品维护
        </h1>
        <p class="page-subtitle">管理产品信息，维护SKU与申报资料</p>
      </div>
      <div class="header-actions">
        <el-button type="success" @click="handleCreate">
          <el-icon><Plus /></el-icon>
          新增产品
        </el-button>
      </div>
    </div>

    <!-- 筛选栏 -->
    <div class="filter-bar">
      <div class="filter-group">
        <el-input
          v-model="searchForm.keyword"
          placeholder="SKU/产品名称/品牌/ASIN"
          clearable
          style="width: 240px"
          @keyup.enter="handleSearch"
        >
          <template #prefix><el-icon><Search /></el-icon></template>
        </el-input>
        <el-select
          v-model="searchForm.status"
          placeholder="状态"
          clearable
          style="width: 120px"
        >
          <el-option label="全部" value="" />
          <el-option label="启用" :value="1" />
          <el-option label="禁用" :value="0" />
        </el-select>
        <el-select
          v-model="searchForm.category_id"
          placeholder="分类"
          clearable
          style="width: 160px"
        >
          <el-option
            v-for="item in categoryList"
            :key="item.id"
            :label="item.category_cn || item.category"
            :value="item.id"
          />
        </el-select>
        <el-button type="primary" @click="handleSearch" :loading="loading">
          <el-icon><Search /></el-icon> 搜索
        </el-button>
        <el-button plain @click="resetSearch">
          <el-icon><Refresh /></el-icon> 重置
        </el-button>
      </div>
    </div>

    <!-- 数据表格 -->
    <div class="table-card">
      <el-table
        :data="productList"
        v-loading="loading"
        style="width: 100%"
        height="calc(100vh - 296px)"
        row-class-name="product-row"
        :header-cell-style="{background:'#f8f9fa',color:'#555',fontWeight:600}"
        :cell-style="{padding:'10px 0'}"
      >
        <el-table-column type="index" label="序号" width="60" align="center" />
        <el-table-column label="主图" width="80" align="center">
          <template #default="scope">
            <el-image
              v-if="scope.row.image_url"
              :src="scope.row.image_url"
              :preview-src-list="[scope.row.image_url]"
              preview-teleported
              fit="cover"
              class="product-thumb-small"
            />
            <div v-else class="thumb-placeholder-small"><el-icon><Picture /></el-icon></div>
          </template>
        </el-table-column>
        <el-table-column prop="seller_sku" label="卖家SKU" min-width="140" show-overflow-tooltip>
          <template #default="scope">
            <span style="font-family:monospace;font-size:13px;font-weight:500;color:#1a1a2e;">{{ scope.row.seller_sku }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="product_name" label="产品名称" min-width="160" show-overflow-tooltip>
          <template #default="scope">
            <span style="font-weight:500;color:#1a1a2e;">{{ scope.row.product_name }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="declare_name_cn" label="申报中文名" min-width="140" show-overflow-tooltip>
          <template #default="scope">
            <span style="color:#555;">{{ scope.row.declare_name_cn }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="declare_name_en" label="申报英文名" min-width="160" show-overflow-tooltip>
          <template #default="scope">
            <span style="color:#555;">{{ scope.row.declare_name_en }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="brand" label="品牌" min-width="100" show-overflow-tooltip>
          <template #default="scope">
            <span style="color:#888;">{{ scope.row.brand }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="model" label="型号" min-width="100" show-overflow-tooltip>
          <template #default="scope">
            <span style="color:#888;font-size:13px;">{{ scope.row.model }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="asin" label="ASIN" min-width="120" show-overflow-tooltip>
          <template #default="scope">
            <span style="font-family:monospace;font-size:12px;color:#888;">{{ scope.row.asin }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="fnsku" label="FNSKU" min-width="120" show-overflow-tooltip>
          <template #default="scope">
            <span style="font-family:monospace;font-size:12px;color:#888;">{{ scope.row.fnsku }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="weight_kg" label="重量(kg)" width="100" align="center">
          <template #default="scope">
            <span style="color:#555;font-size:13px;">{{ scope.row.weight_kg }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="category_id" label="分类" width="120" align="center" show-overflow-tooltip>
          <template #default="scope">
            <el-tag v-if="getCategoryName(scope.row.category_id)" size="small" effect="plain" type="info">{{ getCategoryName(scope.row.category_id) }}</el-tag>
            <span v-else style="color:#bbb;">-</span>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="80" align="center">
          <template #default="scope">
            <el-tag v-if="scope.row.status === 1" type="success" size="small" effect="dark" round>启用</el-tag>
            <el-tag v-else type="danger" size="small" effect="dark" round>禁用</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="创建时间" width="160" align="center">
          <template #default="scope">
            <span style="font-size:12px;color:#888;font-family:monospace;">{{ scope.row.created_at }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="updated_at" label="更新时间" width="160" align="center">
          <template #default="scope">
            <span style="font-size:12px;color:#888;font-family:monospace;">{{ scope.row.updated_at }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="120" fixed="right" align="center">
          <template #default="scope">
            <el-button type="primary" text size="small" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button type="danger" text size="small" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-wrap">
        <el-pagination
          v-model:current-page="pagination.page"
          v-model:page-size="pagination.page_size"
          :page-sizes="[20, 50, 100]"
          :total="pagination.total"
          layout="total, sizes, prev, pager, next, jumper"
          :hide-on-single-page="false"
          @current-change="handlePageChange"
          @size-change="handleSizeChange"
        />
      </div>
    </div>

    <!-- 新增/编辑对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="isEdit ? '编辑产品' : '新增产品'"
      width="920px"
      :destroy-on-close="true"
      :close-on-click-modal="false"
      class="product-dialog"
      align-center
    >
      <el-form :model="formData" label-width="110px" :rules="formRules" ref="formRef">
        <!-- 基础信息 -->
        <el-divider content-position="left">基础信息</el-divider>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="卖家SKU" prop="seller_sku">
              <el-input v-model="formData.seller_sku" placeholder="输入卖家SKU" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="产品名称" prop="product_name">
              <el-input v-model="formData.product_name" placeholder="输入产品名称" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="申报中文名">
              <el-input v-model="formData.declare_name_cn" placeholder="输入申报中文名" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="申报英文名">
              <el-input v-model="formData.declare_name_en" placeholder="输入申报英文名" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="品牌">
              <el-input v-model="formData.brand" placeholder="输入品牌" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="型号">
              <el-input v-model="formData.model" placeholder="输入型号" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="用途">
              <el-input v-model="formData.purpose" placeholder="输入用途" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="中文材质">
              <el-input v-model="formData.material_cn" placeholder="输入中文材质" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="英文材质">
              <el-input v-model="formData.material_en" placeholder="输入英文材质" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="分类">
              <el-select
                v-model="formData.category_id"
                placeholder="选择分类"
                clearable
                style="width: 100%"
              >
                <el-option
                  v-for="item in categoryList"
                  :key="item.id"
                  :label="item.category_cn || item.category"
                  :value="item.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 图片信息 -->
        <el-divider content-position="left">图片信息</el-divider>
        <el-row :gutter="20">
          <el-col :span="24">
            <el-form-item label="主图">
              <el-upload
                v-model:file-list="mainImageFileList"
                action="#"
                :auto-upload="false"
                list-type="picture-card"
                :limit="1"
                :on-preview="handlePictureCardPreview"
                :class="['upload-main', { 'hide-upload': mainImageFileList.length >= 1 }]"
              >
                <el-icon><Plus /></el-icon>
              </el-upload>
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item label="多图">
              <el-upload
                v-model:file-list="imageFilesList"
                action="#"
                :auto-upload="false"
                list-type="picture-card"
                :multiple="true"
                :on-preview="handlePictureCardPreview"
                class="upload-gallery"
              >
                <el-icon><Plus /></el-icon>
              </el-upload>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 海关与物流 -->
        <el-divider content-position="left">海关与物流</el-divider>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="申报货值">
              <el-input-number
                v-model="formData.declare_value"
                :precision="2"
                :min="0"
                :controls="false"
                placeholder="单个产品申报货值"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="币种">
              <el-input v-model="formData.currency" placeholder="如 USD" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="海关编码">
              <el-input v-model="formData.hs_code" placeholder="输入海关编码" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="产品重量(kg)">
              <el-input-number
                v-model="formData.weight_kg"
                :precision="3"
                :min="0"
                :controls="false"
                placeholder="产品重量(kg)"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="产品尺寸(cm)">
              <el-input v-model="formData.dimensions_cm" placeholder="长*宽*高，如 10*5*3" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="是否带电">
              <el-radio-group v-model="formData.is_electric">
                <el-radio :label="1">是</el-radio>
                <el-radio :label="0">否</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="是否带磁">
              <el-radio-group v-model="formData.is_magnetic">
                <el-radio :label="1">是</el-radio>
                <el-radio :label="0">否</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 亚马逊信息 -->
        <el-divider content-position="left">亚马逊信息</el-divider>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="ASIN">
              <el-input v-model="formData.asin" placeholder="输入ASIN" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="FNSKU">
              <el-input v-model="formData.fnsku" placeholder="输入FNSKU" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="亚马逊内部ID">
              <el-input v-model="formData.amazon_internal_id" placeholder="输入亚马逊内部编码ID" />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 税务信息 -->
        <el-divider content-position="left">税务信息</el-divider>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="VAT号码">
              <el-input v-model="formData.vat_number" placeholder="输入VAT号码" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="EORI号码">
              <el-input v-model="formData.eori_number" placeholder="输入EORI号码" />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 其他 -->
        <el-divider content-position="left">其他</el-divider>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="销售链接">
              <el-input v-model="formData.sales_url" placeholder="输入销售链接" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="状态" prop="status">
              <el-radio-group v-model="formData.status">
                <el-radio :label="1">启用</el-radio>
                <el-radio :label="0">禁用</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item label="备注">
              <el-input v-model="formData.remark" type="textarea" :rows="3" placeholder="输入备注" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitLoading">确定</el-button>
      </template>
    </el-dialog>

    <!-- 图片预览 -->
    <el-dialog v-model="previewVisible" width="600px" append-to-body align-center>
      <img :src="previewImageUrl" style="width: 100%; display: block;" />
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { useListQuerySync } from '@/composables/useListQuerySync.js'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Search, Refresh, Plus, Goods, Picture } from '@element-plus/icons-vue'
import {
  getProducts,
  createProductWithFiles,
  updateProductWithFiles,
  deleteProduct,
  getCategoryOptions
} from '@/services/api.js'

export default {
  name: 'ProductView',
  components: {
    Search,
    Refresh,
    Plus,
    Goods,
    Picture
  },
  setup() {
    const loading = ref(false)
    const submitLoading = ref(false)
    const dialogVisible = ref(false)
    const isEdit = ref(false)
    const formRef = ref(null)
    const productList = ref([])

    // 图片上传
    const mainImageFileList = ref([])
    const imageFilesList = ref([])
    const previewVisible = ref(false)
    const previewImageUrl = ref('')

    const searchForm = reactive({
      keyword: '',
      status: '',
      category_id: ''
    })

    const { initFromQuery, syncQuery, watchQuery } = useListQuerySync({
      page: { get: () => pagination.page, set: v => pagination.page = v, type: 'number', default: 1 },
      page_size: { get: () => pagination.page_size, set: v => pagination.page_size = v, type: 'number', default: 20 },
      keyword: { get: () => searchForm.keyword, set: v => searchForm.keyword = v },
      status: { get: () => searchForm.status, set: v => searchForm.status = v },
      category_id: { get: () => searchForm.category_id, set: v => searchForm.category_id = v ? Number(v) : '', type: 'number' }
    })

    const pagination = reactive({
      page: 1,
      page_size: 20,
      total: 0
    })

    const formData = reactive({
      id: null,
      seller_sku: '',
      product_name: '',
      declare_name_cn: '',
      declare_name_en: '',
      remark: '',
      status: 1,
      category_id: '',

      // 材质信息
      material_cn: '',
      material_en: '',

      // 产品属性
      purpose: '',
      brand: '',
      model: '',

      // 申报价值
      declare_value: null,
      currency: 'USD',

      // 海关信息
      hs_code: '',
      is_electric: 0,
      is_magnetic: 0,

      // 亚马逊信息
      amazon_internal_id: '',
      asin: '',
      fnsku: '',

      // 税务信息
      vat_number: '',
      eori_number: '',

      // 其他
      sales_url: '',
      weight_kg: null,
      dimensions_cm: ''
    })

    const formRules = {
      seller_sku: [
        { required: true, message: '请输入卖家SKU', trigger: 'blur' },
        { max: 100, message: '最多100个字符', trigger: 'blur' }
      ],
      product_name: [
        { required: true, message: '请输入产品名称', trigger: 'blur' }
      ]
    }

    const fetchList = async () => {
      syncQuery()
      loading.value = true
      try {
        const params = {
          page: pagination.page,
          page_size: pagination.page_size
        }
        if (searchForm.keyword) params.keyword = searchForm.keyword
        if (searchForm.status !== '') params.status = searchForm.status
        if (searchForm.category_id !== '') params.category_id = searchForm.category_id

        const response = await getProducts(params)
        if (response.data.status === 'success') {
          const data = response.data.data || {}
          productList.value = data.list || []
          pagination.total = data.total || 0
          pagination.page = data.page || 1
          pagination.page_size = data.page_size || 20
        } else {
          ElMessage.error(response.data.message || '获取产品列表失败')
          productList.value = []
          pagination.total = 0
        }
      } catch (error) {
        console.error('获取产品列表失败:', error)
        ElMessage.error('获取产品列表失败: ' + (error.response?.data?.message || error.message))
        productList.value = []
        pagination.total = 0
      } finally {
        loading.value = false
      }
    }

    const handleSearch = () => {
      pagination.page = 1
      fetchList()
    }

    const resetSearch = () => {
      searchForm.keyword = ''
      searchForm.status = ''
      searchForm.category_id = ''
      pagination.page = 1
      fetchList()
    }

    const resetForm = () => {
      formData.id = null
      formData.seller_sku = ''
      formData.product_name = ''
      formData.declare_name_cn = ''
      formData.declare_name_en = ''
      formData.remark = ''
      formData.status = 1
      formData.category_id = ''

      formData.material_cn = ''
      formData.material_en = ''
      formData.purpose = ''
      formData.brand = ''
      formData.model = ''
      formData.declare_value = null
      formData.currency = 'USD'
      formData.hs_code = ''
      formData.is_electric = 0
      formData.is_magnetic = 0
      formData.amazon_internal_id = ''
      formData.asin = ''
      formData.fnsku = ''
      formData.vat_number = ''
      formData.eori_number = ''
      formData.sales_url = ''
      formData.weight_kg = null
      formData.dimensions_cm = ''

      mainImageFileList.value = []
      imageFilesList.value = []
    }

    const handleCreate = () => {
      isEdit.value = false
      resetForm()
      dialogVisible.value = true
    }

    const handleEdit = (row) => {
      isEdit.value = true
      formData.id = row.id
      formData.seller_sku = row.seller_sku || ''
      formData.product_name = row.product_name || ''
      formData.declare_name_cn = row.declare_name_cn || ''
      formData.declare_name_en = row.declare_name_en || ''
      formData.remark = row.remark || ''
      formData.status = row.status ?? 1
      formData.category_id = row.category_id ?? ''

      formData.material_cn = row.material_cn || ''
      formData.material_en = row.material_en || ''
      formData.purpose = row.purpose || ''
      formData.brand = row.brand || ''
      formData.model = row.model || ''
      formData.declare_value = row.declare_value != null ? Number(row.declare_value) : null
      formData.currency = row.currency || 'USD'
      formData.hs_code = row.hs_code || ''
      formData.is_electric = row.is_electric ?? 0
      formData.is_magnetic = row.is_magnetic ?? 0
      formData.amazon_internal_id = row.amazon_internal_id || ''
      formData.asin = row.asin || ''
      formData.fnsku = row.fnsku || ''
      formData.vat_number = row.vat_number || ''
      formData.eori_number = row.eori_number || ''
      formData.sales_url = row.sales_url || ''
      formData.weight_kg = row.weight_kg != null ? Number(row.weight_kg) : null
      formData.dimensions_cm = row.dimensions_cm || ''

      // 回显已有图片
      mainImageFileList.value = row.image_url ? [{ name: 'main', url: row.image_url }] : []
      try {
        const urls = row.image_urls ? JSON.parse(row.image_urls) : []
        imageFilesList.value = Array.isArray(urls)
          ? urls.filter(u => u).map((url, idx) => ({ name: `img_${idx}`, url }))
          : []
      } catch {
        imageFilesList.value = []
      }

      dialogVisible.value = true
    }

    const handleDelete = (row) => {
      ElMessageBox.confirm(`确定删除产品 "${row.product_name || row.seller_sku}" 吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await deleteProduct(row.id)
          if (response.data.status === 'success') {
            ElMessage.success('删除成功')
            await fetchList()
          } else {
            ElMessage.error(response.data.message || '删除失败')
          }
        } catch (error) {
          console.error('删除产品失败:', error)
          ElMessage.error('删除失败: ' + (error.response?.data?.message || error.message))
        }
      }).catch(() => {})
    }

    const handleSubmit = async () => {
      const valid = await formRef.value?.validate().catch(() => false)
      if (!valid) return

      submitLoading.value = true
      try {
        // 构建 FormData，文本字段 + 文件一起提交
        const fd = new FormData()
        fd.append('seller_sku', formData.seller_sku)
        fd.append('product_name', formData.product_name)
        fd.append('declare_name_cn', formData.declare_name_cn || '')
        fd.append('declare_name_en', formData.declare_name_en || '')
        fd.append('remark', formData.remark || '')
        fd.append('status', formData.status)
        fd.append('category_id', formData.category_id != null ? formData.category_id : '')
        fd.append('material_cn', formData.material_cn || '')
        fd.append('material_en', formData.material_en || '')
        fd.append('purpose', formData.purpose || '')
        fd.append('brand', formData.brand || '')
        fd.append('model', formData.model || '')
        fd.append('declare_value', formData.declare_value != null ? formData.declare_value : '')
        fd.append('currency', formData.currency || 'USD')
        fd.append('hs_code', formData.hs_code || '')
        fd.append('is_electric', formData.is_electric)
        fd.append('is_magnetic', formData.is_magnetic)
        fd.append('amazon_internal_id', formData.amazon_internal_id || '')
        fd.append('asin', formData.asin || '')
        fd.append('fnsku', formData.fnsku || '')
        fd.append('vat_number', formData.vat_number || '')
        fd.append('eori_number', formData.eori_number || '')
        fd.append('sales_url', formData.sales_url || '')
        fd.append('weight_kg', formData.weight_kg != null ? formData.weight_kg : '')
        fd.append('dimensions_cm', formData.dimensions_cm || '')

        // 主图：新上传的文件
        mainImageFileList.value.forEach(file => {
          if (file.raw) {
            fd.append('main_image', file.raw)
          }
        })
        // 多图：新上传的文件
        imageFilesList.value.forEach(file => {
          if (file.raw) {
            fd.append('images', file.raw)
          }
        })

        // 编辑时，保留的已有图片 URL 告诉后端
        if (isEdit.value) {
          const existingMain = mainImageFileList.value
            .filter(f => f.url && !f.raw)
            .map(f => f.url)
          const existingGallery = imageFilesList.value
            .filter(f => f.url && !f.raw)
            .map(f => f.url)
          fd.append('existing_image_url', existingMain[0] || '')
          fd.append('existing_image_urls', JSON.stringify(existingGallery))
        }

        let response
        if (isEdit.value) {
          response = await updateProductWithFiles(formData.id, fd)
        } else {
          response = await createProductWithFiles(fd)
        }

        if (response.data.status === 'success') {
          ElMessage.success(isEdit.value ? '编辑成功' : '新增成功')
          dialogVisible.value = false
          await fetchList()
        } else {
          ElMessage.error(response.data.message || (isEdit.value ? '编辑失败' : '新增失败'))
        }
      } catch (error) {
        console.error('提交失败:', error)
        ElMessage.error('提交失败: ' + (error.response?.data?.message || error.message))
      } finally {
        submitLoading.value = false
      }
    }

    const getCategoryName = (categoryId) => {
      if (categoryId == null || categoryId === '') return ''
      const cat = categoryList.value.find(c => c.id === categoryId)
      return cat ? (cat.category_cn || cat.category) : ''
    }

    const handlePictureCardPreview = (uploadFile) => {
      previewImageUrl.value = uploadFile.url || uploadFile.thumbUrl
      previewVisible.value = true
    }

    const handlePageChange = (page) => {
      pagination.page = page
      fetchList()
    }

    const handleSizeChange = (size) => {
      pagination.page_size = size
      pagination.page = 1
      fetchList()
    }

    const categoryList = ref([])

    const fetchCategories = async () => {
      try {
        const response = await getCategoryOptions()
        if (response.data.status === 'success') {
          categoryList.value = response.data.data || []
        }
      } catch (error) {
        console.error('获取产品分类失败:', error)
      }
    }

    watchQuery(() => fetchList())

    onMounted(() => {
      initFromQuery()
      fetchList()
      fetchCategories()
    })

    return {
      loading,
      submitLoading,
      dialogVisible,
      isEdit,
      formRef,
      productList,
      searchForm,
      pagination,
      formData,
      formRules,
      mainImageFileList,
      imageFilesList,
      previewVisible,
      previewImageUrl,
      categoryList,
      handleSearch,
      resetSearch,
      handleCreate,
      handleEdit,
      handleDelete,
      handleSubmit,
      getCategoryName,
      handlePictureCardPreview,
      handlePageChange,
      handleSizeChange
    }
  }
}
</script>

<style scoped>
.product-page {
  max-width: 1600px;
  margin: 0 auto;
  padding: 24px 24px 40px;
}

/* ===== 头部 ===== */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 24px;
}
.page-title {
  font-size: 26px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 6px;
  letter-spacing: -0.5px;
}
.page-subtitle {
  font-size: 14px;
  color: #888;
  margin: 0;
}
.header-actions {
  display: flex;
  gap: 10px;
}

/* ===== 筛选栏 ===== */
.filter-bar {
  background: #fff;
  border-radius: 12px;
  padding: 14px 18px;
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}
.filter-group {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

/* ===== 表格卡片 ===== */
.table-card {
  background: #fff;
  border-radius: 14px;
  padding: 0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.03);
  overflow: hidden;
}
:deep(.el-table) { --el-table-border-color: #f0f0f0; }
:deep(.product-row:hover) { background-color: #fafbff !important; }

/* 分页 */
.pagination-wrap {
  padding: 16px 20px;
  display: flex;
  justify-content: flex-end;
}

/* 对话框样式 */
:deep(.product-dialog .el-dialog__body) {
  padding-top: 10px;
  padding-bottom: 10px;
  max-height: 70vh;
  overflow-y: auto;
}

/* upload 样式 */
:deep(.upload-main .el-upload--picture-card) {
  width: 120px;
  height: 120px;
}

:deep(.upload-main .el-upload-list__item) {
  width: 120px;
  height: 120px;
}

:deep(.upload-main.hide-upload .el-upload--picture-card) {
  display: none;
}

:deep(.upload-gallery .el-upload--picture-card) {
  width: 100px;
  height: 100px;
}

:deep(.upload-gallery .el-upload-list__item) {
  width: 100px;
  height: 100px;
}

.product-thumb-small {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  background: #fafafa;
  cursor: pointer;
}
.thumb-placeholder-small {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: #f5f7fa;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ccc;
  font-size: 18px;
  margin: 0 auto;
}

/* ===== 响应式 ===== */
@media (max-width: 768px) {
  .product-page {
    padding: 16px 16px 40px;
  }
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  .filter-bar {
    flex-direction: column;
    align-items: stretch;
  }
  .filter-group {
    justify-content: stretch;
  }
}
</style>
